import React, { useState, useCallback, useEffect } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { useInputMode } from '../../hooks/useInputMode';
import { useIsNarrow } from '../../hooks/useIsNarrow';
import { useIdleWatcher } from '../../hooks/useIdleWatcher';
import { useAppVersion } from '../../hooks/useAppVersion';
import { IdleWarningModal } from '../IdleWarningModal';
import { Sidebar } from './Sidebar';
import { QuickSearch, openQuickSearch } from './QuickSearch';
import { MobileBottomNav } from './MobileBottomNav';
import { NavigationGuardProvider } from '../common/UnsavedGuard';
import ChangePassword from '../../pages/ChangePassword';
import { Menu, Search } from 'lucide-react';

export function Layout() {
  const { user, loading } = useAuth();
  const inputMode = useInputMode();
  const isNarrow = useIsNarrow();
  // Announces once when the user first loads a release they haven't seen.
  const appVersion = useAppVersion({ announce: true });
  const appEnv = appVersion?.app_env;
  const isPreview = appEnv === 'preview';
  const isEnvUnset = appEnv === 'unknown';
  // 2026-08-23: server-enforced 30-minute idle timeout. Only runs once the
  // user is fully authenticated so it doesn't spin on the login/change-password
  // screens where hitting /auth/heartbeat would 401 in a loop.
  const idle = useIdleWatcher({ enabled: !!user && !user.must_change_password });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Stable callback so Sidebar's dependent effects don't re-fire every Layout render.
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const openSidebar = useCallback(() => setSidebarOpen(true), []);
  // Sidebar mode: 'pinned' (full 256-px grouped sidebar; groups default to collapsed, hover/click to expand) |
  // 'hover' (collapsed 64-px icon rail with flyout overlays). Default: 'pinned'.
  const [mode, setMode] = useState(() => {
    try {
      const raw = localStorage.getItem('sidebar.mode');
      if (raw === 'pinned' || raw === 'hover') return raw;
    } catch {/* no-op */}
    return 'pinned';
  });

  const toggleMode = () => {
    setMode(prev => {
      const next = prev === 'pinned' ? 'hover' : 'pinned';
      try { localStorage.setItem('sidebar.mode', next); } catch {/* no-op */}
      return next;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  if (!user) {
    // v4.37.1 item 6: remember where an unauthenticated deep link was headed
    // (e.g. an evaluation-signing link from email) so SSO can return there.
    try {
      const p = window.location.pathname + window.location.search;
      if (p && p !== '/' && !p.startsWith('/login') && !p.startsWith('/logged-out')) {
        sessionStorage.setItem('postLoginReturnTo', p);
      }
    } catch { /* no-op */ }
    return <Navigate to="/login" replace />;
  }
  if (user.must_change_password) return <ChangePassword />;

  // Drawer mode is driven by touch OR a narrow viewport (2026-09-08). Keying
  // it on pointer type alone meant a 390px-wide window with a mouse still got
  // the 256px pinned sidebar, pushing page content off the right edge.
  const isTouch = inputMode === 'touch';
  const useDrawer = isTouch || isNarrow;
  // Desktop main-content left padding: rail (64px) when hover, full (256px)
  // when pinned. Drawer mode: no padding, the sidebar overlays instead.
  const mainPad = useDrawer ? '' : mode === 'pinned' ? 'pl-64' : 'pl-16';

  return (
    <div className="min-h-screen bg-app">
    <NavigationGuardProvider>
      {isEnvUnset && (
        <div
          className="sticky top-0 z-50 w-full bg-red-600 text-white text-center text-xs font-bold py-1 px-2 tracking-wide"
          data-testid="env-unset-banner"
        >
          APP_ENV is not set. Email is blocked until it is set.
        </div>
      )}
      {isPreview && (
        <div
          className="sticky top-0 z-50 w-full bg-amber-500 text-black text-center text-xs font-bold py-1 px-2 tracking-wide"
          data-testid="preview-env-banner"
        >
          PREVIEW ENVIRONMENT — test data only. Email is restricted to the allowlist.
        </div>
      )}      <Sidebar
        mobileOpen={sidebarOpen}
        onClose={closeSidebar}
        mode={mode}
        onToggleMode={toggleMode}
        forceDrawer={useDrawer}
      />

      {/* Mobile top bar — whenever the sidebar is a drawer, so the menu is
          always reachable. */}
      {useDrawer && (
        <div className="theme-pin-light sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-slate-900 text-white" data-testid="mobile-topbar">
          <button
            type="button"
            onClick={openSidebar}
            className="flex items-center justify-center min-w-[44px] min-h-[44px] -ml-2 hover:bg-slate-800 rounded-md transition-colors"
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          <img
            src="/tricorps360-horizontal-white.png"
            alt="TriCorps 360"
            className="h-8 w-auto object-contain"
            data-testid="mobile-topbar-logo"
          />
          <button
            type="button"
            onClick={openQuickSearch}
            className="ml-auto flex items-center justify-center min-w-[44px] min-h-[44px] -mr-2 hover:bg-slate-800 rounded-md transition-colors"
            data-testid="mobile-search-btn"
            aria-label="Search screens"
          >
            <Search className="w-5 h-5" />
          </button>
        </div>
      )}

      <main className={`transition-all duration-200 ${mainPad}`}>
        <div className={`p-4 sm:p-6 md:p-8 min-h-screen ${useDrawer ? 'pb-24' : ''}`}>
          <AdminOutageBanner user={user} />
          <Outlet />
        </div>
      </main>

      {useDrawer && <MobileBottomNav />}
      <QuickSearch />

      <IdleWarningModal
        open={idle.showWarning}
        secondsRemaining={idle.secondsRemaining}
        onStay={idle.stay}
        onSignOut={idle.signOutNow}
      />
    </NavigationGuardProvider>
    </div>
  );
}


/** Admin-only red banner shown when a CRITICAL feature is unavailable because a
 *  required setting is missing (sign-in, email, SSN/banking decryption). */
function AdminOutageBanner({ user }) {
  const [outages, setOutages] = useState([]);
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    let on = true;
    api.get('/system/feature-status')
      .then(r => { if (on) setOutages((r.data?.features || []).filter(f => f.critical && !f.available)); })
      .catch(() => {});
    return () => { on = false; };
  }, [user]);
  if (!outages.length) return null;
  return (
    <div className="mb-4 rounded-lg border-2 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="admin-critical-outage-banner">
      <p className="font-semibold">Action needed: a critical feature is unavailable in this environment.</p>
      <ul className="mt-1 list-disc pl-5">
        {outages.map(f => (
          <li key={f.id}><strong>{f.name}</strong> — add the missing setting{f.missing.length > 1 ? 's' : ''} {f.missing.join(', ')} in Manage Publishes &gt; Secrets.</li>
        ))}
      </ul>
      <p className="mt-1 text-xs text-red-600">See Administration &gt; System for full status.</p>
    </div>
  );
}
